/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import ErrorLayout from './layouts/Error';
import RestaurantLayout from './layouts/Restaurant';
import DashboardLayout from './layouts/Dashboard/Dashboard';
import Aerospace from './views/Aerospace';
import Aerospace3D from './views/Aerospace3D';
import Geofences from './views/Geofences';
import { MBObstacles } from './views/Obstacles/MBObstacles';
import Orders from './views/Orders';
import Order from './views/Orders/Show';
import DevTools from './views/DevTools';
import OrderHub from './views/Restaurant/OrderHub';
import WeigherApp from './views/Operations/WeigherApp';
import { TimeTickerProvider } from './contexts/TimeTickerContext';
import ShowMission from './views/Mission/ShowMission';
import RoutePlanner from 'views/RoutePlanner';
import Missions from './views/Mission/Missions';
import BomOverview from 'views/BomV2';
import { authHOC } from './views/Auth/authHOC';
import Commercial from 'views/Marketing/Commercial';
import OrdersMap from './views/Restaurant/OrdersMap';
import ProtectedRoute, { MatchLogic } from './components/ProtectedRoute/ProtectedRoute';
import { Role } from './constants/roles';
import CreateFlightTestPlan from './views/FlightTestPlan/CreateFlightTestPlan';
import ViewFlightTestPlans from './views/FlightTestPlan/ViewFlightTestPlans';
import SelectActiveAddresses from './views/Restaurant/SelectActiveAddresses';
import ViewFlightCards from 'views/FlightCard/ViewFlightCards';
import CreateFlightCard from 'views/FlightCard/CreateFlightCard';
import CreateTestOrder from 'views/TestOrders/CreateTestOrder';
import CellInformationList from 'views/CellInformation/CellInformationList';
import MerchantDeviceList from 'views/MerchantDevices/MerchantDeviceList';
import ConfigureMerchantDevice from 'views/MerchantDevices/ConfigureMerchantDevice';
import DropPointSuggestor from './views/DropPointSuggestor/DropPointSuggestor';
import DropPointSuggestorV2 from './views/DropPointSuggestorV2/DropPointSuggestorV2';
import MobileDropAnalyzer from './views/MobileDropAnalyzer/MobileDropAnalyzer';
import DropPointManagement from './views/DropPointManagement/DropPointManagement';
import EarlyTesterListPage from 'views/EarlyTester/EarlyTesterListPage';
import IssuesOverview from 'views/BomV2/IssuesOverview';
import LoaderApp from 'views/Operations/LoaderApp';
import PlaceDropPointTest from './views/PlaceDropPointTest';
import StoreCreation from './views/StoreCreation/StoreCreation';

const RestaurantHOC = authHOC(RestaurantLayout);
const DashboardHOC = authHOC(DashboardLayout);

const Error401 = lazy(() => import('views/Error401'));
const Error404 = lazy(() => import('views/Error404'));
const Error500 = lazy(() => import('views/Error500'));

const routes = (
  <Routes>
    <Route path="/" element={<Navigate to="/aerospace" />} />
    <Route index element={<Navigate to="/aerospace" replace />} />
    <Route 
      path="mobile-drop-analyzer" 
      element={
        <ProtectedRoute
          requiredRoles={[
            Role.Portal_Commercial_CS,
            Role.Portal_Dev_Admin,
            Role.Portal_Ops_Admin,
            Role.Portal_Ops_MC,
            Role.Portal_Admin
          ]}
          matchLogic={MatchLogic.OR}>
          <MobileDropAnalyzer />
        </ProtectedRoute>
      } 
    />
    <Route path="errors" element={<ErrorLayout />}>
    <Route
        path="error-401"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <Error401 />
          </Suspense>
        }
      />
      <Route
        path="error-404"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <Error404 />
          </Suspense>
        }
      />
      <Route
        path="error-500"
        element={
          <Suspense fallback={<div>Loading...</div>}>
            <Error500 />
          </Suspense>
        }
      />
      <Route path="*" element={<Navigate to="/errors/error-404" replace />} />
    </Route>
    <Route path="bom" element={<DashboardHOC />}>
      <Route
        path="overview"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.ROLE_DEPOT_MFG_ADMIN,
              Role.ROLE_DEPOT_MFG_USER,
              Role.ROLE_DEPOT_ENGINEERING_ADMIN,
              Role.ROLE_DEPOT_ENGINEERING_USER,
              Role.ROLE_DEPOT_OPS_ADMIN,
              Role.ROLE_DEPOT_OPS_USER,
              Role.ROLE_DEPOT_ADMIN,
              Role.Portal_Ops_MC,
              Role.Portal_Business_Viewer,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_Support,
              Role.Portal_Ops_GroundTeam
            ]}
            matchLogic={MatchLogic.OR}>
            <BomOverview />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/errors/error-404" replace />} />
    </Route>
    <Route path="bom" element={<RestaurantHOC />}>
      <Route
        path="issues"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.ROLE_DEPOT_MFG_ADMIN,
              Role.ROLE_DEPOT_MFG_USER,
              Role.ROLE_DEPOT_ENGINEERING_ADMIN,
              Role.ROLE_DEPOT_ENGINEERING_USER,
              Role.ROLE_DEPOT_OPS_ADMIN,
              Role.ROLE_DEPOT_OPS_USER,
              Role.ROLE_DEPOT_ADMIN,
              Role.Portal_Ops_MC,
              Role.Portal_Business_Viewer,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_Support,
              Role.Portal_Ops_GroundTeam
            ]}
            matchLogic={MatchLogic.OR}>
            <IssuesOverview />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/errors/error-404" replace />} />
    </Route>
    <Route path="flight-test-plan" element={<DashboardHOC />}>
      <Route
        index
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Admin,
              Role.Portal_Ops_SIM
            ]}
            matchLogic={MatchLogic.OR}>
            <ViewFlightTestPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="new"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Admin,
              Role.Portal_Ops_SIM
            ]}
            matchLogic={MatchLogic.OR}>
            <CreateFlightTestPlan />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/errors/error-404" replace />} />
    </Route>
    <Route path="flight-cards" element={<RestaurantHOC />}>
      <Route
        index
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Admin,
              Role.Portal_Flight_Testing,
              Role.Portal_Pilot
            ]}
            matchLogic={MatchLogic.OR}>
            <ViewFlightCards />
          </ProtectedRoute>
        }
      />
      <Route
        path="new"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Admin,
              Role.Portal_Flight_Testing
            ]}
            matchLogic={MatchLogic.OR}>
            <CreateFlightCard />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/errors/error-404" replace />} />
    </Route>
    <Route path="restaurant" element={<RestaurantHOC />}>
      <Route
        path="orderhub"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_CS,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Business_Viewer,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <TimeTickerProvider>
              <OrderHub useSSEForOrders={false} />
            </TimeTickerProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="orderhubSSE"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_CS,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Business_Viewer,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <TimeTickerProvider>
              <OrderHub useSSEForOrders={true} />
            </TimeTickerProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="ops"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Ops_Support,
              Role.Portal_Ops_GroundTeam,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_CS,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Business_Viewer,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <WeigherApp />
          </ProtectedRoute>
        }
      />
      <Route
        path="ops-v3"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Ops_Support,
              Role.Portal_Ops_GroundTeam,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_CS,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Business_Viewer,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <LoaderApp />
          </ProtectedRoute>
        }
      />
      <Route
        path="map"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_CS,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Business_Viewer,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <OrdersMap />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/errors/error-404" replace />} />
    </Route>
    <Route path="*" element={<DashboardHOC />}>
      <Route
        path="aerospace"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Commercial_Admin,
              Role.Portal_Admin,
              Role.Portal_Business_Viewer
            ]}
            matchLogic={MatchLogic.OR}>
            <Aerospace />
          </ProtectedRoute>
        }
      />
      <Route
        path="aerospace3D"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <Aerospace3D />
          </ProtectedRoute>
        }
      />
      <Route
        path="orders"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_CS,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Business_Viewer,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <Orders />
          </ProtectedRoute>
        }
      />
      <Route
        path="order/:id"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_CS,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Business_Viewer,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <Order />
          </ProtectedRoute>
        }
      />
      <Route
        path="missions"
        element={
          <ProtectedRoute
            requiredRoles={[Role.Portal_Dev_Admin, Role.Portal_Admin]}
            matchLogic={MatchLogic.OR}>
            <Missions />
          </ProtectedRoute>
        }
      />
      <Route
        path="mission/:id"
        element={
          <ProtectedRoute
            requiredRoles={[Role.Portal_Dev_Admin, Role.Portal_Admin]}
            matchLogic={MatchLogic.OR}>
            <ShowMission />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/geofences"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <Geofences />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/route-planner"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <RoutePlanner />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/obstacles"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <MBObstacles />
          </ProtectedRoute>
        }
      />
      <Route
        path="devtools"
        element={
          <ProtectedRoute
            requiredRoles={[Role.Portal_Dev_Admin, Role.Portal_Admin]}
            matchLogic={MatchLogic.OR}>
            <DevTools />
          </ProtectedRoute>
        }
      />
      <Route
        path="test-orders"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <CreateTestOrder />
          </ProtectedRoute>
        }
      />
      <Route
        path="cell-information"
        element={
          <ProtectedRoute
            requiredRoles={[Role.Portal_Dev_Admin, Role.Portal_Admin]}
            matchLogic={MatchLogic.OR}>
            <CellInformationList />
          </ProtectedRoute>
        }
      />
      <Route
        path="merchant-devices"
        element={
          <ProtectedRoute
            requiredRoles={[Role.Portal_Dev_Admin, Role.Portal_Admin, Role.Portal_Commercial_Admin]}
            matchLogic={MatchLogic.OR}>
            <MerchantDeviceList />
          </ProtectedRoute>
        }
      />
      <Route
        path="merchant-devices/configure"
        element={
          <ProtectedRoute
            requiredRoles={[Role.Portal_Dev_Admin, Role.Portal_Admin, Role.Portal_Commercial_Admin]}
            matchLogic={MatchLogic.OR}>
            <ConfigureMerchantDevice />
          </ProtectedRoute>
        }
      />
      <Route
        path="commercial"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Commercial_CS,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <Commercial />
          </ProtectedRoute>
        }
      />
      <Route
        path="store-creation"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Commercial_Admin,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <StoreCreation />
          </ProtectedRoute>
        }
      />
      <Route
        path="early-tester-manager"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Commercial_Admin,
              Role.Portal_Commercial_Marketing,
              Role.Portal_Commercial_CS,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <EarlyTesterListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="selectactiveaddresses"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <SelectActiveAddresses />
          </ProtectedRoute>
        }
      />
      <Route
        path="suggest-drop-points"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Commercial_CS,
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <DropPointSuggestor />
          </ProtectedRoute>
        }
      />
      <Route
        path="suggest-drop-points-v2"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Commercial_CS,
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <DropPointSuggestorV2 />
          </ProtectedRoute>
        }
      />
      <Route
        path="drop-point-management"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Commercial_CS,
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <DropPointManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="place-drop-point-test"
        element={
          <ProtectedRoute
            requiredRoles={[
              Role.Portal_Commercial_CS,
              Role.Portal_Dev_Admin,
              Role.Portal_Ops_Admin,
              Role.Portal_Ops_MC,
              Role.Portal_Admin
            ]}
            matchLogic={MatchLogic.OR}>
            <PlaceDropPointTest />
          </ProtectedRoute>
        }
      />
      <Route path="marketing" element={<Navigate to="/commercial" replace />} />
      <Route path="*" element={<Navigate to="/errors/error-404" replace />} />
    </Route>
  </Routes>
);

export default routes;
